import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import api from "@/services/api";
import { formatPhone } from "@/utils/phoneFormatter";

export type MessagingChannel = "whatsapp" | "telegram";

export interface Connection {
  id: number;
  channel: MessagingChannel;
  phone: string;
  display_name: string;
  linked_at: string;
}

export interface ConnectionsResponse {
  channels: MessagingChannel[];
  connections: Connection[];
  /** How many links each channel takes (WhatsApp several, Telegram one); 1 when left out. */
  limits?: Partial<Record<MessagingChannel, number>>;
}

export interface LinkCode {
  code: string;
  url: string;
}

export type MagicLink =
  | { status: "closed" }
  | {
      status: "open";
      channel: MessagingChannel;
      phone: string;
      display_name: string;
      /** The viewer's links on the channel. */
      linked: Connection[];
      /** How many links the channel takes: past one this link is added, at one it switches. */
      limit: number;
      /** The viewer has as many links as the channel takes: confirming would be refused. */
      at_cap: boolean;
    };

export interface TokenScope {
  id: string;
  label: string;
  warning?: string | null;
}

export interface AccessToken {
  id: number;
  name: string;
  prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
}

export const CHANNEL_NAMES: Record<MessagingChannel, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
};

/**
 * The row's identity: the full number or, where the channel has no number, the account name the
 * sender chose, in quotes so it can't pass for a number.
 */
export function connectionLabel(
  connection: Pick<Connection, "channel" | "phone" | "display_name">
): string {
  if (connection.phone) return formatPhone(connection.phone);
  return connection.display_name
    ? `“${connection.display_name}”`
    : `${CHANNEL_NAMES[connection.channel]} account`;
}

/** How many links `channel` takes; a channel that takes one replaces its link instead. */
export function linkLimit(data: ConnectionsResponse | undefined, channel: MessagingChannel) {
  return data?.limits?.[channel] ?? 1;
}

export const connectionsKey = ["agent", "connections"] as const;
export const tokensKey = ["agent", "tokens"] as const;
export const tokenScopesKey = ["agent", "tokens", "scopes"] as const;
export const magicLinkKey = (token: string) => ["agent", "links", token] as const;

export function useConnections(options: { pollMs?: number } = {}) {
  return useQuery({
    queryKey: connectionsKey,
    queryFn: async () => (await api.get<ConnectionsResponse>("/agent/connections/")).data,
    refetchInterval: options.pollMs ?? false,
  });
}

export async function mintLinkCode(channel: MessagingChannel): Promise<LinkCode> {
  return (await api.post<LinkCode>("/agent/link-codes/", { channel })).data;
}

export function useDisconnect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.post(`/agent/connections/${id}/disconnect/`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: connectionsKey }),
  });
}

// The magic-link token only ever travels in request bodies, never in a URL.
export function useMagicLink(token: string) {
  return useQuery({
    queryKey: magicLinkKey(token),
    queryFn: async () => (await api.post<MagicLink>("/agent/links/peek/", { token })).data,
    enabled: Boolean(token),
    retry: false,
  });
}

export function useConfirmMagicLink(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<Connection>("/agent/links/confirm/", { token })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: connectionsKey }),
    // Refused at the limit, nothing was spent: the peek shows the numbers linked now.
    onError: (error) => {
      if (errorCode(error) === "at_cap") {
        void queryClient.invalidateQueries({ queryKey: magicLinkKey(token) });
        void queryClient.invalidateQueries({ queryKey: connectionsKey });
      }
    },
  });
}

/**
 * Token scopes 404 while MCP connections are off (or the token API is absent); the row then stays
 * hidden.
 */
export function useTokenScopes() {
  return useQuery({
    queryKey: tokenScopesKey,
    queryFn: async () => {
      try {
        return (await api.get<{ scopes: TokenScope[] }>("/agent/tokens/scopes/")).data.scopes;
      } catch (error) {
        if (isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity,
  });
}

export function useTokens(enabled: boolean) {
  return useQuery({
    queryKey: tokensKey,
    queryFn: async () => (await api.get<{ tokens: AccessToken[] }>("/agent/tokens/")).data.tokens,
    enabled,
  });
}

export function useMintToken() {
  const queryClient = useQueryClient();
  return useMutation({
    // The raw token is in the result: keep no copy once the dialog lets go of it.
    gcTime: 0,
    mutationFn: async (body: { name: string; scopes: string[] }) =>
      (await api.post<AccessToken & { token: string }>("/agent/tokens/", body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tokensKey, exact: true }),
  });
}

export function useRevokeToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      try {
        await api.delete(`/agent/tokens/${id}/`);
      } catch (error) {
        // Already gone counts as revoked.
        if (!(isAxiosError(error) && error.response?.status === 404)) throw error;
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: tokensKey, exact: true }),
  });
}

/** The server's code for a failed request ("at_cap"), if it gave one. */
export function errorCode(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  return (error.response?.data as { code?: string } | undefined)?.code;
}

/** The server's message for a failed request, or a generic one. */
export function errorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; detail?: string } | undefined;
    if (data?.message || data?.detail) return (data.message || data.detail) as string;
  }
  return "Something went wrong. Please try again.";
}
